import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Switch,
  Typography,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import UnarchiveOutlinedIcon from "@mui/icons-material/UnarchiveOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ContentShell from "components/ContentShell";
import CustomButton from "components/CustomButton";
import DeleteConsentDialog from "components/DeleteConsentDialog";
import LoadingButton from "components/LoadingButton";
import FilePicker from "components/FilePicker";
import ImagePreviewBox from "components/ImagePreviewBox";
import useConsent from "hooks/useConsent";
import { useSnackbar } from "hooks/useSnackbar";
import {
  Badge,
  archiveBadge,
  bulkReplaceBadges,
  bulkUploadBadges,
  deleteBadge,
  getBadgeUploadUrl,
  getBadges,
  setBadgeActive,
  unarchiveBadge,
} from "services/badgeService";
import { uploadImageV2 } from "services/uploadService";
import { DEFAULT_IMAGE_DIMENSIONS, ImageType } from "utils/imageConstants";

const BADGE_DIMS = DEFAULT_IMAGE_DIMENSIONS[ImageType.TEAM_BADGE];

// One badge card — holds its own pending file, dimensions and live preview.
const BadgeCard = ({
  badge,
  onUpload,
  onArchiveToggle,
  onDelete,
  onActiveToggle,
  selected,
  onSelectToggle,
}: {
  badge: Badge & { archived?: boolean };
  onUpload: (badgeKey: string, file: File, dims: any) => Promise<void>;
  onArchiveToggle: (badgeKey: string, archived: boolean) => Promise<void>;
  onDelete: (badgeKey: string) => Promise<void>;
  onActiveToggle: (badgeKey: string, isActive: boolean) => Promise<void>;
  selected: boolean;
  onSelectToggle: (badgeKey: string) => void;
}) => {
  const { showSnackbar } = useSnackbar();
  const [file, setFile] = useState<File | null>(null);
  const [dims, setDims] = useState<any>(null);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const { isOpenConsent, constentDetails, openConsent, closeConsent, updateDetails } = useConsent();

  // Live object-URL preview of the selected file (cleaned up on change/unmount).
  useEffect(() => {
    if (!file) {
      setPreviewUrl(undefined);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleUpload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      await onUpload(badge.key, file, dims);
      showSnackbar(`Badge ${badge.key} updated.`, "success");
      setFile(null);
      setDims(null);
    } catch (error: any) {
      showSnackbar(error.message || "Failed to update badge", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleActiveToggle = async (checked: boolean) => {
    setActionBusy(true);
    try {
      await onActiveToggle(badge.key, checked);
      showSnackbar(`Badge ${badge.key} ${checked ? "activated" : "deactivated"}.`, "success");
    } catch (error: any) {
      showSnackbar(error.message || "Failed to update badge status", "error");
    } finally {
      setActionBusy(false);
    }
  };

  const handleArchive = async () => {
    closeConsent();
    setActionBusy(true);
    try {
      await onArchiveToggle(badge.key, true);
      showSnackbar(`Badge ${badge.key} archived.`, "success");
    } catch (error: any) {
      showSnackbar(error.message || "Failed to archive badge", "error");
    } finally {
      setActionBusy(false);
    }
  };

  const handleDelete = async () => {
    closeConsent();
    setActionBusy(true);
    try {
      await onDelete(badge.key);
    } catch (error: any) {
      showSnackbar(error.message || "Failed to delete badge", "error");
    } finally {
      setActionBusy(false);
    }
  };

  const requestArchive = () => {
    updateDetails({
      itemName: badge.key,
      message: "Are you sure you want to archive badge ",
      btnText: "Archive",
      onConfirm: handleArchive,
    });
    openConsent();
  };

  const requestDelete = () => {
    updateDetails({
      itemName: badge.key,
      message: "Are you sure you want to delete badge ",
      btnText: "Delete",
      onConfirm: handleDelete,
    });
    openConsent();
  };

  return (
    <Paper
      elevation={0}
      sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", overflow: "hidden" }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2.5,
          py: 1.5,
          borderBottom: "1px solid",
          borderBottomColor: "divider",
        }}
      >
        <Checkbox size="small" checked={selected} onChange={() => onSelectToggle(badge.key)} />
        <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
          {badge.key}
        </Typography>
        {previewUrl && <Chip size="small" color="primary" label="New selected" />}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {badge.active ? "Shown in-game" : "Not shown in-game"}
          </Typography>
          <Switch
            size="small"
            checked={badge.active}
            disabled={actionBusy}
            onChange={(e) => handleActiveToggle(e.target.checked)}
          />
        </Box>
        <IconButton size="small" disabled={actionBusy} onClick={requestArchive} title="Archive badge">
          <ArchiveOutlinedIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" disabled={actionBusy} onClick={requestDelete} title="Delete badge">
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Box>

      <DeleteConsentDialog
        open={isOpenConsent}
        itemName={constentDetails?.itemName}
        message={constentDetails?.message}
        btnText={constentDetails?.btnText}
        onClose={closeConsent}
        onConfirm={constentDetails?.onConfirm}
        loading={actionBusy}
      />

      <Box sx={{ p: 2.5, display: "flex", gap: 3, flexWrap: "wrap", alignItems: "flex-start" }}>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Box sx={{ width: 160 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, fontWeight: 600 }}>
              Current
            </Typography>
            <ImagePreviewBox
              url={badge.url}
              alt={badge.key}
              height={170}
              emptyIcon={<ImageOutlinedIcon fontSize="large" />}
              emptyText="No image"
            />
          </Box>

          {previewUrl && (
            <Box sx={{ width: 160 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, fontWeight: 600 }}>
                New (preview)
              </Typography>
              <ImagePreviewBox url={previewUrl} alt="New badge" height={170} badge={<Chip size="small" color="primary" label="New" />} />
            </Box>
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 280 }}>
          <FilePicker
            // Remount (reset internal file + dimension state) after each save.
            key={`badge-${badge.key}-${badge.url}`}
            label="Choose image"
            name={`badge-${badge.key}`}
            fileType="image/*"
            showPreview={false}
            imageType={ImageType.TEAM_BADGE}
            enableDimensionControl={true}
            metaData={{
              note: `Recommended: ${BADGE_DIMS.width}×${BADGE_DIMS.height}px. Resized to WebP, aspect ratio preserved (not cropped).`,
            }}
            onFileSelect={(f: any, d?: any) => {
              setFile(f || null);
              setDims(d ?? null);
            }}
          />

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 2 }}>
            <LoadingButton
              variant="contained"
              loading={busy}
              loadingPosition="start"
              startIcon={<CloudUploadIcon />}
              disabled={!file || busy}
              onClick={handleUpload}
            >
              {busy ? "Saving..." : "Replace"}
            </LoadingButton>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

// One row in the archived-badges list — thumbnail, key, and an unarchive
// action. No delete/confirm here: archived badges are already hidden from
// players, unarchiving just restores them to the main (inactive) grid.
const ArchivedBadgeRow = ({
  badge,
  onUnarchive,
}: {
  badge: Badge;
  onUnarchive: (badgeKey: string) => Promise<void>;
}) => {
  const { showSnackbar } = useSnackbar();
  const [busy, setBusy] = useState(false);

  const handleUnarchive = async () => {
    setBusy(true);
    try {
      await onUnarchive(badge.key);
      showSnackbar(`Badge ${badge.key} unarchived.`, "success");
    } catch (error: any) {
      showSnackbar(error.message || "Failed to unarchive badge", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: 1,
        px: 2.5,
        borderBottom: "1px solid",
        borderBottomColor: "divider",
      }}
    >
      <ImagePreviewBox
        url={badge.url}
        alt={badge.key}
        width={48}
        height={48}
        emptyIcon={<ImageOutlinedIcon fontSize="small" />}
        emptyText=""
      />
      <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
        {badge.key}
      </Typography>
      <LoadingButton
        size="small"
        loading={busy}
        disabled={busy}
        startIcon={<UnarchiveOutlinedIcon />}
        onClick={handleUnarchive}
      >
        Unarchive
      </LoadingButton>
    </Box>
  );
};

const BulkCreatePanel = ({ onDone }: { onDone: () => Promise<void> }) => {
  const { showSnackbar } = useSnackbar();
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);

  const handleUpload = async () => {
    if (!files.length) return;
    setBusy(true);
    try {
      const resp = await bulkUploadBadges(files);
      const { createdSync, queuedAsync, errors } = resp.data;

      if (queuedAsync) {
        showSnackbar(
          `${createdSync} of ${files.length} badges created so far; the rest are still processing and will finish shortly.`,
          "success",
        );
      } else if (errors.length) {
        showSnackbar(`Created ${createdSync}, failed ${errors.length}: ${errors.map((e) => e.fileName).join(", ")}`, "error");
      } else {
        showSnackbar(`Created ${createdSync} new badge(s).`, "success");
      }

      setFiles([]);
      await onDone();
    } catch (error: any) {
      showSnackbar(error.message || "Bulk create failed", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <CloudUploadIcon sx={{ color: "primary.main" }} />
        <Typography variant="subtitle1" fontWeight={700}>
          Create new badges
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          Each selected file becomes a brand-new badge (created inactive).
        </Typography>
        <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
          <CustomButton component="label" startIcon={<CloudUploadIcon />} disabled={busy}>
            {files.length ? `${files.length} file(s) selected` : "Choose files"}
            <input
              hidden
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
            />
          </CustomButton>
          <LoadingButton
            variant="contained"
            loading={busy}
            loadingPosition="start"
            disabled={busy || !files.length}
            onClick={handleUpload}
          >
            {busy ? "Creating..." : `Create ${files.length || ""}`}
          </LoadingButton>
        </Box>
      </Box>
    </Paper>
  );
};

const BulkReplacePanel = ({
  selectedKeys,
  onDone,
}: {
  selectedKeys: string[];
  onDone: () => Promise<void>;
}) => {
  const { showSnackbar } = useSnackbar();
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);

  const mismatched = files.length > 0 && files.length !== selectedKeys.length;

  const handleUpload = async () => {
    if (!selectedKeys.length || mismatched) return;
    setBusy(true);
    try {
      const resp = await bulkReplaceBadges(selectedKeys, files);
      const { createdSync, queuedAsync, errors } = resp.data;

      if (queuedAsync) {
        showSnackbar(`${createdSync} of ${files.length} replaced so far; the rest are still processing.`, "success");
      } else if (errors.length) {
        showSnackbar(`Replaced ${createdSync}, failed ${errors.length}.`, "error");
      } else {
        showSnackbar(`Replaced ${createdSync} badge(s).`, "success");
      }

      setFiles([]);
      await onDone();
    } catch (error: any) {
      showSnackbar(error.message || "Bulk replace failed", "error");
    } finally {
      setBusy(false);
    }
  };

  if (!selectedKeys.length) return null;

  return (
    <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <CloudUploadIcon sx={{ color: "primary.main" }} />
        <Typography variant="subtitle1" fontWeight={700}>
          Bulk replace ({selectedKeys.length} selected)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          Files are matched to selected badges in the order chosen below.
        </Typography>
        <Box sx={{ ml: "auto", display: "flex", gap: 1 }}>
          <CustomButton component="label" startIcon={<CloudUploadIcon />} disabled={busy}>
            {files.length ? `${files.length} file(s) selected` : "Choose files"}
            <input
              hidden
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
            />
          </CustomButton>
          <LoadingButton
            variant="contained"
            loading={busy}
            loadingPosition="start"
            disabled={busy || !files.length || mismatched}
            onClick={handleUpload}
          >
            {busy ? "Replacing..." : `Replace ${selectedKeys.length}`}
          </LoadingButton>
        </Box>
      </Box>
      {mismatched && (
        <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
          {files.length} file(s) selected but {selectedKeys.length} badge(s) selected: these must match.
        </Typography>
      )}
    </Paper>
  );
};

const TeamBadges = () => {
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  // Holds every badge including archived ones (fetched with includeArchived).
  // `badges` below is the derived non-archived view the rest of this screen
  // already assumes; `archivedBadges` feeds the collapsible archived section.
  const [allBadges, setAllBadges] = useState<Badge[]>([]);
  const badges = allBadges.filter((b) => !b.archived);
  const archivedBadges = allBadges.filter((b) => b.archived);
  const [showArchived, setShowArchived] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [bulkActionBusy, setBulkActionBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState<{
    verb: string;
    succeededCount: number;
    groups: { message: string; keys: string[] }[];
  } | null>(null);
  const {
    isOpenConsent: isOpenBulkConsent,
    constentDetails: bulkConsentDetails,
    openConsent: openBulkConsent,
    closeConsent: closeBulkConsent,
    updateDetails: updateBulkConsentDetails,
  } = useConsent();

  const toggleSelect = (badgeKey: string) =>
    setSelectedKeys((prev) => (prev.includes(badgeKey) ? prev.filter((k) => k !== badgeKey) : [...prev, badgeKey]));

  const allSelected = badges.length > 0 && selectedKeys.length === badges.length;
  const someSelected = selectedKeys.length > 0 && !allSelected;
  const toggleSelectAll = () => setSelectedKeys(allSelected ? [] : badges.map((b) => b.key));

  // `isInitialLoad` only drives ContentShell's full-page loader for the very
  // first fetch. Every later refetch (after a toggle/archive/delete/upload)
  // updates `badges` in the background - the action that triggered it already
  // has its own inline/card-level spinner, so swapping the whole page out
  // again would be a jarring, redundant "full reload" feel.
  const fetchBadges = async (isInitialLoad = false) => {
    try {
      if (isInitialLoad) setLoading(true);
      const resp = await getBadges(true);
      // The S3 key is deterministic, so a replaced badge reuses the same URL and the browser
      // would serve the cached old one. Append a cache-buster per fetch to force the latest.
      const stamp = Date.now();
      const list: Badge[] = (resp?.data || []).map((b: Badge) => ({
        ...b,
        url: b.url ? `${b.url.split("?")[0]}?t=${stamp}` : b.url,
      }));
      setAllBadges(list);
    } catch (error: any) {
      showSnackbar(error.message || "Failed to load badges", "error");
    } finally {
      if (isInitialLoad) setLoading(false);
    }
  };

  useEffect(() => {
    fetchBadges(true);
  }, []);

  // Shared by single-card replace (with picked dims) and bulk (defaults).
  // Does not refetch — callers decide when to refresh (bulk refreshes once at the end).
  const uploadBadge = async (badgeKey: string, file: File, dims?: any) => {
    const uploadResp = await getBadgeUploadUrl(badgeKey);
    const uploadUrl = uploadResp?.data?.uploadImageUrl;
    if (!uploadUrl) throw new Error("Could not obtain an upload URL.");

    const result = await uploadImageV2({
      data: file,
      uploadUrl,
      resize: true,
      resizeToWidth: dims?.targetWidth || BADGE_DIMS.width,
      resizeToHeight: dims?.targetHeight || BADGE_DIMS.height,
    });
    if (!result?.status) {
      throw new Error(result?.message || "Image upload failed.");
    }
  };

  // Single-card replace refreshes immediately after its one upload.
  const uploadBadgeAndRefresh = async (badgeKey: string, file: File, dims?: any) => {
    await uploadBadge(badgeKey, file, dims);
    await fetchBadges();
  };

  const handleActiveToggle = async (badgeKey: string, isActive: boolean) => {
    await setBadgeActive(badgeKey, isActive);
    await fetchBadges();
  };

  const handleArchiveToggle = async (badgeKey: string) => {
    await archiveBadge(badgeKey);
    await fetchBadges();
  };

  const handleUnarchive = async (badgeKey: string) => {
    await unarchiveBadge(badgeKey);
    await fetchBadges();
  };

  const handleDelete = async (badgeKey: string) => {
    const resp = await deleteBadge(badgeKey);
    // Game BE's deleteBadge returns CommonResultDTO { message, data: { success } } -
    // the success flag is nested one level deeper than message.
    if (resp?.data?.data?.success) {
      showSnackbar(`Badge ${badgeKey} deleted.`, "success");
      await fetchBadges();
    } else {
      showSnackbar(resp?.data?.message || `Could not delete badge ${badgeKey}.`, "error");
    }
  };

  // Backend failure messages embed the badge key inline (e.g. `Cannot delete
  // badge "badge1": it is still active...`), so every message string is
  // actually unique per badge - grouping by raw text would never group
  // anything. Classify into the known reason categories instead, so 20
  // badges blocked for the same underlying cause collapse into one group.
  const categorizeFailureMessage = (message: string): string => {
    if (message.includes("still active")) return "Still active — deactivate or archive it first, then delete.";
    if (message.includes("currently have it equipped")) {
      return "Still equipped by players — archive it instead, or wait until usage drops to zero.";
    }
    if (message.includes("must be unarchived")) return "Archived — unarchive it first.";
    if (message.includes("Unknown badge key")) return "Badge no longer exists.";
    return message;
  };

  const groupFailuresByMessage = (failed: { key: string; message: string }[]) => {
    const byCategory = new Map<string, string[]>();
    failed.forEach(({ key, message }) => {
      const category = categorizeFailureMessage(message);
      byCategory.set(category, [...(byCategory.get(category) || []), key]);
    });
    return Array.from(byCategory.entries()).map(([message, keys]) => ({ message, keys }));
  };

  // Shared by the three bulk actions below: fires one request per selected
  // key (each badge succeeds/fails independently, there's no batch endpoint
  // for these - they're cheap single-document writes, not image uploads).
  // On any failure, results go to bulkResult (a compact dialog) instead of
  // a snackbar - a snackbar listing 20 near-identical reasons is unreadable.
  const runBulkAction = async (
    action: (key: string) => Promise<void>,
    verb: string,
  ): Promise<{ succeeded: string[]; failed: { key: string; message: string }[] }> => {
    const results = await Promise.allSettled(selectedKeys.map((key) => action(key)));
    const succeeded: string[] = [];
    const failed: { key: string; message: string }[] = [];

    results.forEach((result, i) => {
      const key = selectedKeys[i];
      if (result.status === "fulfilled") {
        succeeded.push(key);
      } else {
        failed.push({ key, message: result.reason?.message || `Failed to ${verb}` });
      }
    });

    return { succeeded, failed };
  };

  const reportBulkResult = (verb: string, succeededCount: number, failed: { key: string; message: string }[]) => {
    if (!failed.length) {
      showSnackbar(`${succeededCount} badge(s) ${verb}.`, "success");
    } else {
      setBulkResult({ verb, succeededCount, groups: groupFailuresByMessage(failed) });
    }
    // Keep only the failed/blocked keys selected so the admin can see exactly
    // which badges the bulk action didn't reach (succeeded ones either vanish
    // from the list - delete - or simply lose their checkmark relevance).
    setSelectedKeys(failed.map((f) => f.key));
  };

  const bulkSetActive = async (isActive: boolean) => {
    setBulkActionBusy(true);
    try {
      const verb = isActive ? "activated" : "deactivated";
      const { succeeded, failed } = await runBulkAction((key) => setBadgeActive(key, isActive), verb);
      reportBulkResult(verb, succeeded.length, failed);
      await fetchBadges();
    } finally {
      setBulkActionBusy(false);
    }
  };

  const bulkArchive = async () => {
    setBulkActionBusy(true);
    try {
      const { succeeded, failed } = await runBulkAction((key) => archiveBadge(key), "archived");
      reportBulkResult("archived", succeeded.length, failed);
      await fetchBadges();
    } finally {
      setBulkActionBusy(false);
    }
  };

  // Deletion is gated server-side (won't delete an active badge or one still
  // equipped by users) - this loops per key, so some selected badges may
  // delete while others are blocked. Blocked keys stay selected after the
  // refetch (deleted ones simply vanish from the list), so the admin sees
  // exactly which badges weren't removed and why.
  const bulkDelete = async () => {
    setBulkActionBusy(true);
    try {
      const responses = await Promise.allSettled(selectedKeys.map((key) => deleteBadge(key)));
      const failed: { key: string; message: string }[] = [];
      let succeeded = 0;

      responses.forEach((result, i) => {
        const key = selectedKeys[i];
        if (result.status === "fulfilled" && result.value?.data?.data?.success) {
          succeeded++;
        } else {
          const message = result.status === "fulfilled" ? result.value?.data?.message : result.reason?.message;
          failed.push({ key, message: message || "Failed to delete" });
        }
      });

      reportBulkResult("deleted", succeeded, failed);
      await fetchBadges();
    } finally {
      setBulkActionBusy(false);
    }
  };

  const requestBulkAction = (verb: string, run: () => Promise<void>) => {
    updateBulkConsentDetails({
      itemName: `${selectedKeys.length} selected badge(s)`,
      message: `Are you sure you want to ${verb} `,
      btnText: verb.charAt(0).toUpperCase() + verb.slice(1),
      onConfirm: () => {
        closeBulkConsent();
        run();
      },
    });
    openBulkConsent();
  };

  const activeCount = badges.filter((b) => b.active).length;
  const inactiveCount = badges.length - activeCount;

  return (
    <ContentShell title="Team Badges" loading={loading}>
      <Paper
        elevation={0}
        sx={{ p: 2.5, mb: 3, borderRadius: 3, border: "1px solid", borderColor: "divider" }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
          <ImageOutlinedIcon sx={{ color: "primary.main" }} />
          <Typography variant="subtitle1" fontWeight={700}>
            Standard team badges
          </Typography>
          <Chip
            size="small"
            label={`${activeCount} of ${badges.length} shown in-game`}
            sx={{ ml: "auto", fontWeight: 600 }}
          />
        </Box>
        <Typography variant="body2" color="text.secondary">
          Create, replace, activate, archive, or delete team badges. New badges
          are created inactive — toggle a badge active to show it to players.
          Changes are shared across all environments that use this image bucket.
        </Typography>
        {inactiveCount > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {activeCount} of {badges.length} badges are currently shown to players.
            The remaining {inactiveCount} are stored and replaceable but inactive.
          </Alert>
        )}
      </Paper>

      <BulkCreatePanel onDone={fetchBadges} />
      <BulkReplacePanel
        selectedKeys={selectedKeys}
        onDone={async () => {
          setSelectedKeys([]);
          await fetchBadges();
        }}
      />

      {archivedBadges.length > 0 && (
        <Paper elevation={0} sx={{ mb: 3, borderRadius: 3, border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2.5, py: 1.5, cursor: "pointer" }}
            onClick={() => setShowArchived((prev) => !prev)}
          >
            <ArchiveOutlinedIcon sx={{ color: "text.secondary" }} />
            <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
              Archived badges
            </Typography>
            <Chip size="small" label={archivedBadges.length} />
            <IconButton size="small">
              {showArchived ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          <Collapse in={showArchived}>
            <Typography variant="body2" color="text.secondary" sx={{ px: 2.5, pb: 1.5 }}>
              Hidden from players and from the grid above. Unarchive to bring a badge back (as inactive).
            </Typography>
            {archivedBadges.map((badge) => (
              <ArchivedBadgeRow key={badge.key} badge={badge} onUnarchive={handleUnarchive} />
            ))}
          </Collapse>
        </Paper>
      )}

      {badges.length > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
          <Checkbox size="small" checked={allSelected} indeterminate={someSelected} onChange={toggleSelectAll} />
          <Typography variant="body2" color="text.secondary">
            {allSelected ? "Deselect all" : "Select all"} ({selectedKeys.length}/{badges.length} selected)
          </Typography>

          {selectedKeys.length > 0 && (
            <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
              <LoadingButton
                size="small"
                loading={bulkActionBusy}
                disabled={bulkActionBusy}
                onClick={() => requestBulkAction("activate", () => bulkSetActive(true))}
              >
                Activate selected
              </LoadingButton>
              <LoadingButton
                size="small"
                loading={bulkActionBusy}
                disabled={bulkActionBusy}
                onClick={() => requestBulkAction("deactivate", () => bulkSetActive(false))}
              >
                Deactivate selected
              </LoadingButton>
              <LoadingButton
                size="small"
                loading={bulkActionBusy}
                disabled={bulkActionBusy}
                startIcon={<ArchiveOutlinedIcon />}
                onClick={() => requestBulkAction("archive", bulkArchive)}
              >
                Archive selected
              </LoadingButton>
              <LoadingButton
                size="small"
                color="error"
                loading={bulkActionBusy}
                disabled={bulkActionBusy}
                startIcon={<DeleteOutlineIcon />}
                onClick={() => requestBulkAction("delete", bulkDelete)}
              >
                Delete selected
              </LoadingButton>
            </Box>
          )}
        </Box>
      )}

      <DeleteConsentDialog
        open={isOpenBulkConsent}
        itemName={bulkConsentDetails?.itemName}
        message={bulkConsentDetails?.message}
        btnText={bulkConsentDetails?.btnText}
        onClose={closeBulkConsent}
        onConfirm={bulkConsentDetails?.onConfirm}
        loading={bulkActionBusy}
      />

      {/* Bulk-action result: grouped by failure reason so 20 badges blocked for
          the same cause show as one line + key list, not 20 repeated sentences. */}
      <Dialog open={!!bulkResult} onClose={() => setBulkResult(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk {bulkResult?.verb} results</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: bulkResult?.groups.length ? 2 : 0 }}>
            {bulkResult?.succeededCount} badge(s) {bulkResult?.verb}.
          </Typography>
          {bulkResult?.groups.map((group) => (
            <Box key={group.message} sx={{ mb: 1.5 }}>
              <Typography variant="body2" fontWeight={600}>
                {group.keys.length} not {bulkResult.verb}: {group.message}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {group.keys.join(", ")}
              </Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkResult(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2.5,
        }}
      >
        {badges.map((badge) => (
          <BadgeCard
            key={badge.key}
            badge={badge}
            onUpload={uploadBadgeAndRefresh}
            onActiveToggle={handleActiveToggle}
            onArchiveToggle={handleArchiveToggle}
            onDelete={handleDelete}
            selected={selectedKeys.includes(badge.key)}
            onSelectToggle={toggleSelect}
          />
        ))}
      </Box>
    </ContentShell>
  );
};

export default TeamBadges;
